import React, { useEffect } from 'react';
import { Layout, Menu, Button, Dropdown, message } from 'antd';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  GoldOutlined,
  LogoutOutlined,
  UserOutlined,
  DownOutlined,
  VideoCameraOutlined,
  ShopOutlined,
  TeamOutlined,
  PictureOutlined,
  SwapOutlined,
  FileTextOutlined,
  ImportOutlined,
  WalletOutlined,
  CreditCardOutlined,
  GiftOutlined,
  CrownOutlined
} from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import { authApi } from '../../services/api';

const { Header, Sider, Content } = Layout;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companySlug } = useParams();
  const { admin, logout, companyName, setCompanyName } = useAuthStore();

  useEffect(() => {
    const checkLogin = async () => {
      try {
        const res = await authApi.profile();
        if (res.data?.companyName) {
          setCompanyName(res.data.companyName);
        }
      } catch {
        logout();
        navigate(`/${companySlug}/admin/login`);
      }
    };
    checkLogin();
  }, []);

  const handleLogout = () => {
    authApi.logout().finally(() => {
      logout();
      message.success('已退出登录');
      navigate(`/${companySlug}/admin/login`);
    });
  };

  const prefix = `/${companySlug}`;

  const menuItems = [
    {
      key: `${prefix}/admin`,
      icon: <DashboardOutlined />,
      label: '概览',
    },
    {
      key: `${prefix}/admin/products`,
      icon: <ShoppingOutlined />,
      label: '货品管理',
    },
    {
      key: `${prefix}/admin/products/import`,
      icon: <ImportOutlined />,
      label: '批量导入',
    },
    {
      key: `${prefix}/admin/batches`,
      icon: <AppstoreOutlined />,
      label: '批次管理',
    },
    {
      key: `${prefix}/admin/jade-types`,
      icon: <GoldOutlined />,
      label: '料种管理',
    },
    {
      key: `${prefix}/admin/ys7-devices`,
      icon: <VideoCameraOutlined />,
      label: '监控设备',
    },
    {
      key: `${prefix}/admin/stores`,
      icon: <ShopOutlined />,
      label: '门店管理',
    },
    {
      key: `${prefix}/admin/store-accounts`,
      icon: <TeamOutlined />,
      label: '门店账户',
    },
    {
      key: `${prefix}/admin/departments`,
      icon: <AppstoreOutlined />,
      label: '部门管理',
    },
    {
      key: `${prefix}/admin/department-accounts`,
      icon: <TeamOutlined />,
      label: '部门账户',
    },
    {
      key: `${prefix}/admin/categories`,
      icon: <AppstoreOutlined />,
      label: '品类管理',
    },
    {
      key: `${prefix}/admin/photo-types`,
      icon: <PictureOutlined />,
      label: '照片类型管理',
    },
    {
      key: `${prefix}/admin/photo-type-presets`,
      icon: <PictureOutlined />,
      label: '图片常用语',
    },
    {
      key: `${prefix}/admin/returns`,
      icon: <SwapOutlined />,
      label: '调货记录',
    },
    {
      key: `${prefix}/admin/customers`,
      icon: <UserOutlined />,
      label: '客户管理',
    },
    {
      key: `${prefix}/admin/audit-logs`,
      icon: <FileTextOutlined />,
      label: '系统日志',
    },
    {
      key: `${prefix}/admin/wechat-pay`,
      icon: <WalletOutlined />,
      label: '微信支付配置',
    },
    {
      key: `${prefix}/admin/member-cards`,
      icon: <CreditCardOutlined />,
      label: '会员卡',
    },
    {
      key: `${prefix}/admin/banners`,
      icon: <PictureOutlined />,
      label: '首页轮播',
    },
    {
      key: `${prefix}/admin/point-products`,
      icon: <GiftOutlined />,
      label: '积分商品',
    },
    {
      key: `${prefix}/admin/wx-members`,
      icon: <CrownOutlined />,
      label: '会员管理',
    },
    {
      key: `${prefix}/admin/point-redemptions`,
      icon: <FileTextOutlined />,
      label: '积分订单',
    },
    {
      key: `${prefix}/admin/consultants`,
      icon: <TeamOutlined />,
      label: '顾问管理',
    },
  ];

  const selectedKey = menuItems.find(item => 
    location.pathname === item.key || location.pathname.startsWith(item.key + '/')
  )?.key || `${prefix}/admin`;

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        theme="light"
        width={200}
        style={{
          boxShadow: '2px 0 8px rgba(0,0,0,0.06)',
          zIndex: 10
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <span style={{
            fontSize: 18,
            fontWeight: 600,
            color: '#8B7355'
          }}>{companyName || '和田玉溯源'}</span>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0, paddingTop: 8 }}
        />
      </Sider>
      
      <Layout>
        <Header style={{
          background: '#fff',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          zIndex: 9
        }}>
          <h3 style={{ margin: 0, color: '#1a1a1a', fontWeight: 500 }}>
            管理后台
          </h3>
          
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Button type="text">
              <UserOutlined />
              <span style={{ marginLeft: 8 }}>{admin?.nickname || admin?.username}</span>
              <DownOutlined style={{ marginLeft: 8, fontSize: 12 }} />
            </Button>
          </Dropdown>
        </Header>
        
        <Content style={{
          margin: 24,
          padding: 24,
          background: '#fff',
          borderRadius: 8,
          minHeight: 280,
          overflow: 'auto'
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
